import { Search } from "lucide-react"
import { useMemo, useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import type {
  CategoryOption,
  ContentStatus,
  PaginationMeta,
  StudioGuideItem,
  StudioPoiRow,
  StudioUrls,
} from "@/lib/types"

import { PoiSheet } from "./PoiSheet"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type StatusFilter = "all" | "approved" | "pending" | "needs_changes"

interface PoisTabProps {
  pois: { records: StudioPoiRow[]; meta: PaginationMeta }
  guide_items: StudioGuideItem[]
  categories: CategoryOption[]
  urls: StudioUrls
  editingPoi: StudioPoiRow | null | "new"
  onEditingPoiChange: (poi: StudioPoiRow | null | "new") => void
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "approved", label: "Approved" },
  { value: "pending", label: "Pending" },
  { value: "needs_changes", label: "Needs changes" },
]

function statusBadgeVariant(status: ContentStatus) {
  switch (status) {
    case "approved":
      return "default" as const
    case "pending":
    case "draft":
      return "secondary" as const
    case "needs_changes":
      return "outline" as const
    case "rejected":
    case "archived":
      return "destructive" as const
    default:
      return "secondary" as const
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  })
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PoisTab({
  pois,
  guide_items,
  categories,
  urls,
  editingPoi,
  onEditingPoiChange,
}: PoisTabProps) {
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all")

  // Build a map of poi_id -> guide position for "In guide" column
  const poiGuidePosition = useMemo(() => {
    const map = new Map<number, number>()
    for (const item of guide_items) {
      if (item.poi) {
        map.set(item.poi.id, item.position)
      }
    }
    return map
  }, [guide_items])

  // Status counts for filter pills
  const statusCounts = useMemo(() => {
    const counts = { all: 0, approved: 0, pending: 0, needs_changes: 0 }
    for (const poi of pois.records) {
      counts.all++
      if (poi.status === "approved") counts.approved++
      else if (poi.status === "pending") counts.pending++
      else if (poi.status === "needs_changes") counts.needs_changes++
    }
    return counts
  }, [pois.records])

  // Filtered POIs
  const filteredPois = useMemo(() => {
    let result = pois.records

    // Status filter
    if (statusFilter !== "all") {
      result = result.filter((p) => p.status === statusFilter)
    }

    // Search filter (client-side, by name)
    if (search.trim()) {
      const q = search.toLowerCase()
      result = result.filter((p) => p.name.toLowerCase().includes(q))
    }

    return result
  }, [pois.records, statusFilter, search])

  // Sheet state
  const sheetOpen = editingPoi !== null
  const sheetPoi = editingPoi === "new" ? null : editingPoi

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative w-64">
              <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
              <Input
                placeholder="Search POIs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 pl-9 text-sm"
              />
            </div>

            {/* Status pills */}
            <div className="flex items-center gap-1.5">
              {STATUS_FILTERS.map((f) => (
                <Button
                  key={f.value}
                  variant={statusFilter === f.value ? "default" : "outline"}
                  size="sm"
                  className="h-7 px-2.5 text-xs"
                  onClick={() => setStatusFilter(f.value)}
                >
                  {f.label}
                  <span className="ml-1 opacity-60">{statusCounts[f.value]}</span>
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {filteredPois.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16">
          <p className="text-sm text-muted-foreground">No POIs match these filters</p>
        </div>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>POI</TableHead>
                <TableHead>Categories</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>In guide</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPois.map((poi) => {
                const guidePos = poiGuidePosition.get(poi.id)
                return (
                  <TableRow
                    key={poi.id}
                    role="button"
                    tabIndex={0}
                    aria-label={`Edit ${poi.name}`}
                    className="cursor-pointer focus:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none"
                    onClick={() => onEditingPoiChange(poi)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        onEditingPoiChange(poi)
                      }
                    }}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {poi.cover_url ? (
                          <img
                            src={poi.cover_url}
                            alt=""
                            className="size-9 shrink-0 rounded-md object-cover"
                          />
                        ) : (
                          <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted text-xs text-muted-foreground">
                            --
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium">{poi.name}</div>
                          {poi.address && (
                            <div className="truncate text-xs text-muted-foreground">
                              {poi.address}
                            </div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {poi.categories.slice(0, 2).map((c) => (
                          <Badge key={c.id} variant="outline" className="text-[10px]">
                            {c.name}
                          </Badge>
                        ))}
                        {poi.categories.length > 2 && (
                          <Badge variant="outline" className="text-[10px]">
                            +{poi.categories.length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusBadgeVariant(poi.status)} className="text-xs capitalize">
                        {poi.status.replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {guidePos != null ? (
                        <span className="text-sm font-medium">#{guidePos}</span>
                      ) : (
                        <span className="text-sm text-muted-foreground">--</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(poi.updated_at)}
                      </span>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* POI Sheet — keyed so useForm re-initializes when the selected POI changes */}
      <PoiSheet
        key={sheetPoi ? sheetPoi.id : "new"}
        open={sheetOpen}
        onOpenChange={(open) => {
          if (!open) onEditingPoiChange(null)
        }}
        poi={sheetPoi}
        categories={categories}
        urls={urls}
      />
    </div>
  )
}
